import React, {
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { classNames } from '../lib/classNames';

interface IFixedDimensionsGridProps {
  children: React.ReactNode;
  className?: string;
  itemContentSelector?: string;
  itemVerticalChromePx?: number;
}

/**
 * Makes all direct child elements the same height as the tallest child.
 * Child content can be measured more accurately by marking a descendant
 * element that matches `itemContentSelector`.
 */
export function FixedDimensionsGrid({
  children,
  className,
  itemContentSelector = `[data-fixed-dimensions-content="true"]`,
  itemVerticalChromePx = 14,
}: IFixedDimensionsGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [fixedItemHeight, setFixedItemHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const gridElement = gridRef.current;

    if (!gridElement) {
      return;
    }

    const updateUniformHeight = () => {
      const itemElements = Array.from(
        gridElement.querySelectorAll<HTMLElement>(`[data-fixed-dimensions-item="true"]`),
      );

      if (!itemElements.length) {
        setFixedItemHeight(null);

        return;
      }

      const maxHeight = itemElements.reduce((largest, element) => {
        const contentElement = element.querySelector<HTMLElement>(itemContentSelector);
        const contentHeight = contentElement
          ? contentElement.getBoundingClientRect().height
          : element.scrollHeight;

        return Math.max(largest, contentHeight + itemVerticalChromePx);
      }, 0);

      const nextHeight = maxHeight ? Math.ceil(maxHeight) : null;

      setFixedItemHeight(previousHeight => {
        return previousHeight === nextHeight ? previousHeight : nextHeight;
      });
    };

    updateUniformHeight();

    const resizeObserver = typeof ResizeObserver === `undefined`
      ? null
      : new ResizeObserver(updateUniformHeight);
    resizeObserver?.observe(gridElement);

    const mutationObserver = typeof MutationObserver === `undefined`
      ? null
      : new MutationObserver(updateUniformHeight);

    mutationObserver?.observe(gridElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [itemContentSelector, itemVerticalChromePx]);

  const renderedChildren = React.Children.map(children, child => {
    if (!React.isValidElement(child)) {
      return child;
    }

    const childElement = child as React.ReactElement<Record<string, unknown>>;

    if (childElement.type === React.Fragment) {
      return childElement;
    }

    const childProps = childElement.props as {
      style?: React.CSSProperties;
      [key: string]: unknown;
    };

    return React.cloneElement(childElement, {
      ...childProps,
      'data-fixed-dimensions-item': `true`,
      style: fixedItemHeight ? { ...childProps.style, height: `${fixedItemHeight}px` } : childProps.style,
    });
  });

  return (
    <div
      ref={ gridRef }
      className={ classNames(className) }
    >
      { renderedChildren }
    </div>
  );
}
